import { BUILD_RECORD_SOURCES } from '../ProcessingData.js';
import z from 'zod';
/**
 * A project's build-record rows (DR-5094): every unit an aircraft or engine delivery record lists
 * as fitted at build. A build record is the factory birth evidence of an original-fit part. Rinoj's
 * rule (2026-10-14): an accessory marked ORIG (original fit) on an accessory list needs no release
 * certificate; its birth evidence is the delivery record that lists it — the Airbus "Aircraft
 * Inspection Report" Chapter 3 Equipment pages (`airbus-aircraft-inspection-report`, this release)
 * and the IAE "VSL Serialized Parts List" (`vsl-serialized-parts-list`, added by DR-5100).
 *
 * Emitted as ONE webhook per project carrying a flat array, mirroring the ARC family: every
 * delivery-record entity contributes. Each row keeps the `documentId` of its source document, the
 * `pageId` it was extracted from and its `reconstructedDocumentId`. One row per installed unit;
 * `serialNumber` is always present. Oil/v2 (DR-5095) reads these rows beside the ARC rows and
 * links them to accessory rows by exact serial.
 *
 * `esn` is the project's working engine — informational only; rows are NOT scoped to it.
 *
 * Rollout: Brain sends this family only while `BUILD_RECORD_EVENTS_READY_ENABLED=true` (off by
 * default), because Oil rejects an unknown `type`. An empty `buildRecordEvents` array retracts the
 * project's stored rows.
 */
export const buildRecordEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('build-record-events-ready'),
    projectId: z.string(),
    // Same rollout concession as `arc-events-ready`: Brain always emits both stamps (DR-3278,
    // DR-3342); they are optional so a consumer that upgrades ahead of Brain still parses.
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    esn: z.string().nullable(),
    buildRecordEvents: z.array(z.looseObject({
        source: z.enum(BUILD_RECORD_SOURCES),
        partNumber: z.string(),
        serialNumber: z.string(),
        description: z.string(),
        vendor: z.string().optional(),
        vendorCode: z.string().optional(),
        ataChapter: z.string().optional(),
        // Full IPC / VSL locator; VSL rows only.
        ipcReference: z.string().optional(),
        zone: z.string().optional(),
        functionalItemNumber: z.string().optional(),
        // VSL "LL" column; omitted when unreadable, never defaulted to false.
        llFlag: z.boolean().optional(),
        aircraftMsn: z.string().optional(),
        aircraftType: z.string().optional(),
        // VSL header engine serial and model; absent on Airbus rows.
        esn: z.string().optional(),
        engineModel: z.string().optional(),
        // DR-5161: the delivery thrust rating and the unit's printed clocks; absent when not printed.
        thrustRating: z.string().optional(),
        tsn: z.number().nonnegative().optional(),
        csn: z.number().int().nonnegative().optional(),
        reportDate: z.iso.date().optional(),
        section: z.string().optional(),
        // The PRINTED page number and page total, not the PDF page index.
        pageNumber: z.number().int().optional(),
        pageCount: z.number().int().optional(),
        // 1-based printed line on the page, and 1-based unit position within that line.
        rowIndex: z.number().int().positive(),
        unitIndex: z.number().int().positive(),
        documentId: z.string(),
        pageId: z.string(),
        reconstructedDocumentId: z.string().optional(),
    })),
});
//# sourceMappingURL=build-record-events-ready.webhook.js.map